import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, Layers, FileText } from 'lucide-react';
interface SlideCommercialModelProps {
  onContinue: () => void;
}
export function SlideCommercialModel({
  onContinue
}: SlideCommercialModelProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 800, 1400, 2000].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto bg-slate-50" data-id="element-380" data-mp="2caokr24zuw7y">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-3 shrink-0" data-id="element-381" data-mp="2798zb8ywmnp1">
        <p className="text-[#6C2BD9] font-semibold tracking-widest uppercase text-sm" data-id="element-382" data-mp="vw6h69h5pq2r">
          Commercial & Governance Model
        </p>
        <h2 className="text-3xl md:text-5xl font-serif text-slate-900" data-id="element-383" data-mp="qyn3baodffbc">
          Finding a pricing model so it's affordable also for the smaller markets
        </h2>
        <p className="text-lg text-slate-500" data-id="element-384" data-mp="3525w7bx88mna">
          Different market economics, one agreement, minimal administrative
          friction.
        </p>
      </motion.div>

      <div className="flex-1 min-h-0 flex flex-col justify-center max-w-6xl mx-auto w-full mt-8 space-y-6" data-id="element-385" data-mp="2w17ytchanddo">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-386" data-mp="2hc21xv5xhz8y">
          {/* Key markets */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: stage >= 1 ? 1 : 0,
          y: stage >= 1 ? 0 : 16
        }} className="bg-white border border-[#6C2BD9]/20 rounded-2xl p-6 md:p-8 shadow-sm" data-id="element-387" data-mp="2m4v88nh1pfn5">
            <div className="flex items-center gap-4 mb-6" data-id="element-388" data-mp="1yf0ve5wq7r2w">
              <div className="w-12 h-12 rounded-xl bg-[#6C2BD9]/10 text-[#6C2BD9] flex items-center justify-center shrink-0" data-id="element-389" data-mp="23dlq5pt7lijy">
                <Star className="w-6 h-6" data-id="element-390" data-mp="1kaa1dclawd95" />
              </div>
              <div data-id="element-391" data-mp="1fhqoyiycno5x">
                <h3 className="text-slate-900 font-bold text-xl" data-id="element-392" data-mp="1fh8kiz1ja3mx">
                  Key markets
                </h3>
                <p className="text-[#6C2BD9] text-[11px] font-bold uppercase tracking-widest mt-1" data-id="element-393" data-mp="1aj71ge43y4bg">
                  Higher investment
                </p>
              </div>
            </div>
            <ul className="space-y-4" data-id="element-394" data-mp="2372vtc67fk52">
              {['Higher campaign frequency', 'Deeper localization', 'More strategic involvement', 'Higher service level'].map(item => <li key={item} className="flex items-center gap-3" data-id="element-395" data-mp="1oau0glpfpqxx">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#6C2BD9] shrink-0" data-id="element-396" data-mp="1ocoj1td8h6yu" />
                  <span className="text-slate-700" data-id="element-397" data-mp="hs5hlzpgzkll">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          {/* Secondary & legacy markets */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          y: stage >= 2 ? 0 : 16
        }} className="bg-white border border-[#6C2BD9]/20 rounded-2xl p-6 md:p-8 shadow-sm" data-id="element-398" data-mp="2ok3i8nrnhkul">
            <div className="flex items-center gap-4 mb-6" data-id="element-399" data-mp="3397w5srex2ww">
              <div className="w-12 h-12 rounded-xl bg-[#6C2BD9]/10 text-[#6C2BD9] flex items-center justify-center shrink-0" data-id="element-400" data-mp="385q38za7tdce">
                <Layers className="w-6 h-6" data-id="element-401" data-mp="3ca64gn8vvwla" />
              </div>
              <div data-id="element-402" data-mp="3h8hfv2etuf5m">
                <h3 className="text-slate-900 font-bold text-xl" data-id="element-403" data-mp="3h7z8b9mpseie">
                  Secondary & legacy markets
                </h3>
                <p className="text-[#6C2BD9] text-[11px] font-bold uppercase tracking-widest mt-1" data-id="element-404" data-mp="1mt551ufohe3c">
                  Low marginal cost
                </p>
              </div>
            </div>
            <ul className="space-y-4" data-id="element-405" data-mp="3i9d6z4995wkc">
              {['Greater reuse of central assets', 'Standardized adaptation', 'Minimal affiliate involvement', 'Low market-level investment'].map(item => <li key={item} className="flex items-center gap-3" data-id="element-406" data-mp="3r7xjfno7za04">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#6C2BD9] shrink-0" data-id="element-407" data-mp="3n9rhqu28nkm2" />
                  <span className="text-slate-700" data-id="element-408" data-mp="2zi2g2n3j1jsg">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* Global agreement banner */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 3 ? 1 : 0,
        y: stage >= 3 ? 0 : 12
      }} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 flex items-start gap-4" data-id="element-409" data-mp="38bfkomaye9ud">
          <div className="mt-1 text-slate-400 shrink-0" data-id="element-410" data-mp="2bs84e8uy219s">
            <FileText className="w-6 h-6" data-id="element-411" data-mp="21pcoxlqgmxbr" />
          </div>
          <p className="text-slate-700 text-lg leading-relaxed" data-id="element-412" data-mp="21nrvjzfvtkfh">
            A <span className="font-bold text-slate-900" data-id="element-413" data-mp="26gb3ogvsvncw">global or regional agreement</span> removes per-country PO creation, vendor onboarding and administrative friction — the barriers that stop smaller markets adopting at all.
          </p>
        </motion.div>

        {/* Governance table */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 4 ? 1 : 0,
        y: stage >= 4 ? 0 : 12
      }} className="bg-white border border-slate-200 rounded-2xl p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-slate-100" data-id="element-414" data-mp="2kfkltm3kgrji">
          <div className="pt-4 sm:pt-0 sm:px-4 first:pl-0 first:pt-0" data-id="element-415" data-mp="z1zpa7d0x5mp">
            <p className="text-[#6C2BD9] text-[11px] font-bold uppercase tracking-widest mb-2" data-id="element-416" data-mp="141kp0k00pi0h">
              Who decides
            </p>
            <p className="text-slate-700 text-sm leading-relaxed" data-id="element-417" data-mp="36oj2zteo7sht">
              LEO global brand teams — brands, markets and priorities
            </p>
          </div>
          <div className="pt-4 sm:pt-0 sm:px-4" data-id="element-418" data-mp="2wyg63i4fhue7">
            <p className="text-[#6C2BD9] text-[11px] font-bold uppercase tracking-widest mb-2" data-id="element-419" data-mp="2wx533kit4qo5">
              Who executes
            </p>
            <p className="text-slate-700 text-sm leading-relaxed" data-id="element-420" data-mp="2rxip38v5n6ea">
              Snakker — build, localize, activate, optimize
            </p>
          </div>
          <div className="pt-4 sm:pt-0 sm:px-4" data-id="element-421" data-mp="2ixvmijqovujn">
            <p className="text-[#6C2BD9] text-[11px] font-bold uppercase tracking-widest mb-2" data-id="element-422" data-mp="2dzkca7whfi0w">
              Who approves
            </p>
            <p className="text-slate-700 text-sm leading-relaxed" data-id="element-423" data-mp="291iurrepfqrj">
              LEO medical & regulatory, with local sign-off on market specifics
            </p>
          </div>
          <div className="pt-4 sm:pt-0 sm:px-4" data-id="element-424" data-mp="78m9zcuzt9u8">
            <p className="text-[#6C2BD9] text-[11px] font-bold uppercase tracking-widest mb-2" data-id="element-425" data-mp="79xj7s77j9sq">
              Who pays
            </p>
            <p className="text-slate-700 text-sm leading-relaxed" data-id="element-426" data-mp="jrgo5x5cob3x">
              Global or regional agreement funds the engine; markets fund incremental campaigns
            </p>
          </div>
        </motion.div>
      </div>
    </div>;
}